import React from "react";
import dayjs from "dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import useResponsive from "../../../hooks/useResponsive";
import { FeedbackQuestionEvaluationDto, ReferentsEvaluationDto } from "../../../types/workshopTypes";

interface WorkshopInstanceEvaluationAccordionsProps {
  feedbackQuestionsEvaluation: FeedbackQuestionEvaluationDto[];
  referentsEvaluation: ReferentsEvaluationDto[];
}

/**
 * This component is responsible for rendering the accordions for the workshop instance evaluation page.
 * All questions and referents are displayed with the respective grades
 * @param workshopInstanceFeedbackFragen the questions of the workshop instance feedback
 * @param workshopInstanceFeedbackReferenten the referents of the workshop instance feedback
 * @returns the accordions for the workshop instance evaluation page
 */
const WorkshopInstanceEvaluationAccordions: React.FunctionComponent<WorkshopInstanceEvaluationAccordionsProps> = ({
  feedbackQuestionsEvaluation,
  referentsEvaluation,
}: WorkshopInstanceEvaluationAccordionsProps) => {
  const isMobile = useResponsive("down", "sm");

  return feedbackQuestionsEvaluation ? (
    <>
      <Box>
        <Typography fontWeight={"bold"} fontSize={18} sx={{ mb: 1 }}>
          Fragen:
        </Typography>
        {feedbackQuestionsEvaluation.map((question, index) => {
          return (
            <Accordion disableGutters key={`Frage-${index}`}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack spacing={isMobile ? 8 : 3} direction={"row"}>
                  <Box sx={{ display: "flex" }}>
                    <Typography fontWeight={"bold"}>Frage {index + 1}: &nbsp;</Typography>
                    {isMobile ? null : <Typography>{question.question}</Typography>}
                  </Box>
                  <Box sx={{ display: "flex" }}>
                    <Typography color={"primary.main"} fontWeight={"bold"}>
                      Ø
                    </Typography>
                    <Typography color={"primary.main"} fontWeight={"bold"}>
                      &nbsp;{question.average ? question.average.toFixed(1) : "-"}
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Divider />
                {isMobile ? <Typography>{question.question}</Typography> : null}
                <TableContainer sx={{ maxWidth: 350, margin: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Datum</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Bewertung
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {question.feedbacks.map((feedback) => (
                        <TableRow key={feedback.workshopFeedbackId}>
                          <TableCell>{dayjs(feedback.date).format("DD.MM.YYYY")}</TableCell>
                          <TableCell align="right">{feedback.grade === null ? "-" : feedback.grade}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
      {referentsEvaluation.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography fontWeight={"bold"} fontSize={18} sx={{ mb: 1 }}>
            Referenten:
          </Typography>
          {referentsEvaluation.map((referent, index) => {
            return (
              <Accordion disableGutters key={`Referent-${index}`}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack spacing={isMobile ? 8 : 3} direction={"row"}>
                    <Typography fontWeight={"bold"}>
                      {referent.firstName} {referent.lastName}
                    </Typography>
                    <Box sx={{ display: "flex" }}>
                      <Typography color={"primary.main"} fontWeight={"bold"}>
                        Ø
                      </Typography>
                      <Typography color={"primary.main"} fontWeight={"bold"}>
                        &nbsp;{referent.average ? referent.average.toFixed(1) : "-"}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Divider />
                  <TableContainer sx={{ maxWidth: 350, margin: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>Datum</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            Bewertung
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referent.feedbacks.map((feedback) => (
                          <TableRow>
                            <TableCell>{dayjs(feedback.date).format("DD.MM.YYYY")}</TableCell>
                            <TableCell align="right">{feedback.grade === null ? "-" : feedback.grade}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : null}
    </>
  ) : null;
};

export default WorkshopInstanceEvaluationAccordions;
